import type { Invoice } from "@nlc/shared-types";

import { useEffect } from "react";

import { InvoicesFooter } from "./components/InvoicesFooter";
import { InvoicesList } from "./components/InvoicesList";
import { InvoicesSkeleton } from "./components/InvoicesSkeleton";
import { InvoicesTable } from "./components/InvoicesTable";
import { useCopyInvoiceLink } from "./hooks/useCopyInvoiceLink";
import { useInvoices } from "./hooks/useInvoices";

interface InvoicesTabProps {
  readonly limit: number;
  readonly onIssueCredit?: (invoice: Invoice) => void;
  readonly onLimitChange: (limit: number) => void;
  readonly onPageChange: (page: number) => void;
  readonly page: number;
  readonly projectId: number;
}

export function InvoicesTab({
  limit,
  onIssueCredit,
  onLimitChange,
  onPageChange,
  page,
  projectId,
}: InvoicesTabProps) {
  const { data, isError, isPending, isPlaceholderData, refetch } = useInvoices({
    limit,
    page,
    projectId,
  });
  const lastPage = data?.pagination.totalPages ?? 0;

  useEffect(() => {
    if (!isPlaceholderData && lastPage > 0 && page > lastPage) {
      onPageChange(lastPage);
    }
  }, [isPlaceholderData, lastPage, onPageChange, page]);

  const handleCopyLink = useCopyInvoiceLink(projectId);

  if (isPending) {
    return <InvoicesSkeleton />;
  }

  if (isError) {
    return (
      <div className="p-xl">
        <p className="text-sm text-text-secondary">Failed to load invoices.</p>
        <button
          className="mt-md text-sm text-button-primary-bg hover:underline"
          onClick={() => refetch()}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (data.total === 0) {
    return (
      <div className="p-xl">
        <p className="text-sm text-text-tertiary">No invoices yet</p>
      </div>
    );
  }

  return (
    <div>
      <div className="hidden pt-xl md:block">
        <InvoicesTable
          invoices={data.items}
          onCopyLink={handleCopyLink}
          onIssueCredit={onIssueCredit}
        />
      </div>

      <div className="md:hidden">
        <InvoicesList
          invoices={data.items}
          key={`${page}-${limit}`}
          onCopyLink={handleCopyLink}
          onIssueCredit={onIssueCredit}
        />
      </div>

      <InvoicesFooter
        limit={limit}
        onLimitChange={onLimitChange}
        onPageChange={onPageChange}
        page={page}
        totalPages={data.pagination.totalPages}
      />
    </div>
  );
}
